import { FormikErrors } from "formik";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { Link } from "react-router";
import useFileNameLength from "../../../../hooks/useFileNameLength";
import { useReopenedStages } from "../../../../hooks/useReopenedStages";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../../store";
import { TUploadedDocumentsData } from "../../../../store/onboarding/types";
import { clearStage2ReopenThunk } from "../../../../store/superadmin/thunks";
import {
  ICandidateDetails,
  ICandidateReviewFormInput,
  ReferooDocument,
} from "../../../../store/superadmin/types";
import { updateDocumentDateThunk } from "../../../../store/uploads/thunks";
import { TFileUploadsResponse } from "../../../../store/uploads/types";
import { formatDateToReadable, getDateAfterYears, getDateLabel, getDocumentDate, getDocumentName, getMinDateByDocumentType, getValidityMonths, isIssuedType, isOneMonthBefore } from "../../../../utils/documentHelper";
import { getLatestStage2Action } from "../../../../utils/stage2ActionHelper";
import { formatFileName } from "../../../../utils/stringUtils";
import SaveNotification from "../../../SaveNotification";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Download from "../../../UI/Icons/Download";
import FileUpload from "../../../UI/Icons/FileUpload";
import Warning from "../../../UI/Icons/Warning";
import CustomDateInput from "../../../UI/Inputs/DateInput";
import DocumentUpload from "../../../UI/Inputs/DocumentUpload";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
import ViewReferroDetails from "./Popup/ViewReferroDetails";

interface ComplianceCheckProps {
  showComplianceCheck: boolean;
  values: ICandidateReviewFormInput;
  skipComplianceCheck: number;
  setSkipComplianceCheck: (value: number) => void;
  setFieldValue: (field: string, value: string | number) => void;
  currentStage: number;
  reviewCandidateData: ICandidateDetails | null;
  handleFileSelect: (file: TFileUploadsResponse, type: string) => void;
  setIsDisabled: (value: boolean) => void;
  reviewActionValue?: string;
  setIsAdminApproveReferoo?: (value: boolean) => void;
  errors: FormikErrors<ICandidateReviewFormInput>;
}

const ComplianceCheck: React.FC<ComplianceCheckProps> = ({
  showComplianceCheck,
  setIsDisabled,
  values,
  skipComplianceCheck,
  setSkipComplianceCheck,
  setFieldValue,
  handleFileSelect,
  currentStage,
  reviewCandidateData,
  reviewActionValue,
  setIsAdminApproveReferoo,
  errors,
}) => {
  const dispatch = useDispatch();

  const isReferooDocumentFound = useSelector(
    (state) => state.superadmin.referooDocumentsFound
  );
  const referooRightToWorkDoc = useSelector(
    (state) => state.superadmin.referooRightToWorkDoc
  );
  const referooReferenceCheckDoc = useSelector(
    (state) => state.superadmin.referooReferenceCheckDoc
  );

  const rightToWorkDoc = useSelector(
    (state) => state.superadmin.rightToWorkDoc
  );

   const policeCheckDoc = useSelector(
    (state) => state.superadmin.policeCheckDoc
  );

  const candidateVerificationStatus = reviewCandidateData?.profile.verification_status;
  const lastAction = reviewCandidateData?.profile?.profile_steps[0]?.action;
  const reviewCandidateDocumentList = reviewCandidateData?.documents || [];
  const expiringDocumentsCollections = reviewCandidateData?.documents_expire_one_month_documents;
  const isDocumentGoingToExpire = reviewCandidateData?.documents_expire_one_month;
  const isDocumentExpired = reviewCandidateData?.is_documents_expired;

  const [showReferroDetailsModal, setShowReferroDetailsModal] = useState<boolean>(false);
  const [pdfDocument, setPdfDocument] = useState<string>("");
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [pcValidUntil, setPcValidUntil] = useState<Date | null>(null);
  const [wccValidUntil, setWccValidUntil] = useState<Date | null>(null);
   const { maxLength, preserveEndChars } = useFileNameLength();
  const referooData = reviewCandidateData?.referro_data;
  const allCheckType = referooData?.all_check_types || [];
  const completedCheckType = referooData?.completed_check_types || [];
  const pendingCheckType = allCheckType.filter((type) => !completedCheckType.includes(type));

  const { isSectionReopenedFn, reopenedSections } = useReopenedStages();
  const isAdminCompleteReopened = isSectionReopenedFn('admin_complete');
  const isCandidateCompleteReopened = isSectionReopenedFn('candidate_complete');

  const latestStage2Action = getLatestStage2Action(
    reviewCandidateData?.profile?.profile_steps ?? [],
    reviewCandidateData?.reopened_stages_status,
    reviewCandidateData?.referoo_candidate?.created_at
  );



  const matchedMultipleTitles: string[] = [];
  const expiredTitles: string[] = [];

  const documentLabels: Record<string, string> = {
    police_check: "Police Check",
    working_with_children_check: "Working With Children Check / Blue Card",
  };
  
  expiringDocumentsCollections?.forEach((doc) => {
    const match = ['police_check', 'working_with_children_check'].find((config) => config === doc.document_type);

    if (!match) return;

    const title = documentLabels[match]; // ✅ Get readable name
    const isExpired = doc.is_expired === 1;

    if (isExpired) {
      expiredTitles.push(title);
    } else {
      matchedMultipleTitles.push(title);
    }
  });

  const formattedExpired = expiredTitles.join(", ");
  const formattedMultipleTitles = matchedMultipleTitles.join(", ");

  const getFileName = (type: string) => {
    const reviewDocument = reviewCandidateDocumentList.find(
      (file: TUploadedDocumentsData) => file.document_type === type
    );
    return reviewDocument?.file_name || "";
  };

  const validDocumentTypes = [
    "police_check",
    "working_with_children_check",
    "right_to_work",
    "reference_check",
    "reference_check_1",
  ];

  const policeCheck = reviewCandidateData?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "police_check"
  );
  const wccComplianceCheck = reviewCandidateData?.documents?.find(
    (doc: TUploadedDocumentsData) => doc.document_type === "working_with_children_check"
  );

  const pc_valid_until_str = policeCheck?.issued_date;
  const wcc_valid_until_str = wccComplianceCheck?.validity_date;

  useEffect(() => {
    const pc_valid_until = pc_valid_until_str && pc_valid_until_str !== ""
      ? new Date(pc_valid_until_str)
      : null;
    
    const wcc_valid_until =
      wcc_valid_until_str && wcc_valid_until_str !== ""
        ? new Date(wcc_valid_until_str)
        : null;
      
    if (!wccComplianceCheck) {
      setWccValidUntil(null);
    } else {
      setWccValidUntil(wcc_valid_until)
    }

    if (!policeCheck) {
      setPcValidUntil(null);
    } else {
      setPcValidUntil(pc_valid_until)
    }
  }, [
    wccComplianceCheck, 
    policeCheck,
    pc_valid_until_str,
    wcc_valid_until_str
  ]);

  const handleDocDate = (type: string, val: Date | null) => {
    if (!val) return;

    const year = val.getFullYear();
    const month = String(val.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(val.getDate()).padStart(2, '0');

    const dateOnly = `${year}-${month}-${day}`; // Local date in YYYY-MM-DD
    dispatch(updateDocumentDateThunk({ document_type: type, date: dateOnly, candidate_uuid: reviewCandidateData?.uuid, isAdmin: true }));
  };

  useEffect(() => {
    if (
      currentStage === 2 &&
      lastAction === "approve_candidate_bypass_compliance_check"
    ) {
      let hasMissingDocument = false;
      validDocumentTypes.forEach((type: string) => {
        const reviewDocument = reviewCandidateDocumentList.find(
          (file: TUploadedDocumentsData) => file.document_type === type
        );
        if (!reviewDocument?.file_name) {
          hasMissingDocument = true;
        }
      });
      setIsDisabled(hasMissingDocument);
    } else {
      setIsDisabled(false);
    }
  }, [reviewCandidateData]);

  useEffect(() => {
    let checkMissingDocument = false;
    validDocumentTypes.forEach((type: string) => {
      const reviewDocument = reviewCandidateDocumentList.find(
        (file: TUploadedDocumentsData) => file.document_type === type
      );
      if (!reviewDocument?.file_name) {
        checkMissingDocument = true;
      }
    });

    if (lastAction === "approve_candidate_bypass_compliance_check") {
      if (reviewActionValue === "decline") {
        setIsDisabled(false);
      } else if (checkMissingDocument) {
        setIsDisabled(true);
      }
    }
  }, [showComplianceCheck, values.bypass_compliance_check, reviewActionValue]);

  const onClickClose = () => {
    setShowReferroDetailsModal(false);
    setPdfDocument("");
  };

  const handleReferooDoc = (pdfUrl: string) => {
    setPdfDocument(pdfUrl);
    setShowReferroDetailsModal(true);
  };

  const clearStage2CalledRef = useRef<boolean>(false);
  const stateSnapshotAtReopenRef = useRef<{
    fileNames: Record<string, string>;
    pcDate: string | null | undefined;
    wccDate: string | null | undefined;
  } | null>(null);

  const REQUIRED_COMPLIANCE_TYPES = ["police_check", "working_with_children_check", "right_to_work", "reference_check", "reference_check_1"];

  // Snapshot the document state when reopen is activated; clear guards on close
  useEffect(() => {
    if (isAdminCompleteReopened) {
      const docs = reviewCandidateData?.documents || [];
      const fileNames: Record<string, string> = {};
      REQUIRED_COMPLIANCE_TYPES.forEach((type) => {
        const doc = docs.find((d) => d.document_type === type);
        fileNames[type] = doc?.file_name || "";
      });
      const pcDoc = docs.find((d) => d.document_type === "police_check");
      const wccDoc = docs.find((d) => d.document_type === "working_with_children_check");
      stateSnapshotAtReopenRef.current = {
        fileNames,
        pcDate: pcDoc?.issued_date,
        wccDate: wccDoc?.validity_date,
      };
    } else {
      stateSnapshotAtReopenRef.current = null;
      clearStage2CalledRef.current = false;
    }
  }, [isAdminCompleteReopened]);

  useEffect(() => {
    if (!reviewCandidateData || !isAdminCompleteReopened || clearStage2CalledRef.current) return;

    const docs = reviewCandidateData?.documents || []; 

    const allUploaded = REQUIRED_COMPLIANCE_TYPES.every((type) => {
      const doc = docs.find((d) => d.document_type === type);
      return doc?.file_name && doc.file_name !== "";
    });

    const pcDoc = docs.find((d) => d.document_type === "police_check");
    const wccDoc = docs.find((d) => d.document_type === "working_with_children_check");
    const hasPcDate = !!pcDoc?.issued_date;
    const hasWccDate = !!wccDoc?.validity_date;

    if (!allUploaded || !hasPcDate || !hasWccDate) return;

    // Only proceed if something actually changed since the reopen snapshot
    const snapshot = stateSnapshotAtReopenRef.current;
    if (snapshot) {
      const hasFileChange = REQUIRED_COMPLIANCE_TYPES.some((type) => {
        const doc = docs.find((d) => d.document_type === type);
        return (doc?.file_name || "") !== snapshot.fileNames[type];
      });
      const hasPcDateChange = pcDoc?.issued_date !== snapshot.pcDate;
      const hasWccDateChange = wccDoc?.validity_date !== snapshot.wccDate;

      if (!hasFileChange && !hasPcDateChange && !hasWccDateChange) return;
    }

    clearStage2CalledRef.current = true;
    dispatch(clearStage2ReopenThunk({
      candidate_id: reviewCandidateData.profile.id,
      candidate_uuid: reviewCandidateData.uuid,
    }));
  }, [reviewCandidateData?.documents, reviewCandidateData?.profile?.id]);

  const complianceCheckDocs = reviewCandidateDocumentList
    ?.filter(
      (doc) =>
        validDocumentTypes.includes(
          doc.document_type
        )
    );
  
  const sortedComplianceCheckDocs = [...complianceCheckDocs].sort((a, b) => {
    const indexA = validDocumentTypes.indexOf(a.document_type);
    const indexB = validDocumentTypes.indexOf(b.document_type);

    // If both are in the preferred list, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the list, it comes first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // Otherwise, keep original order
    return 0;
  });

  console.log("latestStage2Action", (latestStage2Action !== "admin-complete") , !isAdminCompleteReopened, policeCheck)



  return (
    <>
      {showNotification && (
        <SaveNotification
          message={"No Documents Found"}
          onClose={() => setShowNotification(false)}
        />
      )}

      <ViewReferroDetails
        showReferroDetailsModal={showReferroDetailsModal}
        onClickClose={onClickClose}
        pdfDocument={pdfDocument}
        // metadata={reviewCandidateData?.referoo_candidate?.metadata || null}
      />

      <div
        className={`d-flex flex-column gap-20 fw-400 text-black ${
          !showComplianceCheck ? "d-none" : ""
        }`}
      >
        <div className="fs-22">Compliance Check</div>

        {candidateVerificationStatus !== "verified" && (
          <>
            {
              ((latestStage2Action === "approve" || latestStage2Action === "referoo") && reviewCandidateData?.referoo_status !== "complete") || (latestStage2Action === "approve" && isCandidateCompleteReopened) ? (
                <div className="fs-16">
                  Employee has been requested to{" "}
                  <span className="fw-700">complete this step themselves.</span>
                </div>
              ) : null
            }

            {
              lastAction === "approve_candidate_bypass_compliance_check" ? (
                <div className="fs-16">
                  Admin to complete this step on behalf of employee.
                </div>
              ) : null
            }

            {
              (reviewCandidateData?.referoo_status === "Not initiated" && latestStage2Action === "approve") || (isCandidateCompleteReopened && latestStage2Action === "approve") ? (
              <div className="fs-16">
                Employee has not generated their Referoo link yet. 
              </div>
            ) : null}

            {
              (reviewCandidateData?.referoo_status === "pending" || reviewCandidateData?.referoo_status === "in_progress") && lastAction !== "approve_candidate_bypass_compliance_check" && completedCheckType?.length > 0 ? (
              <div className="fs-16 max-w-484px">
                Employee has generated their Referoo link. {`${completedCheckType?.length > 0 ? `These items are completed: ${completedCheckType.join(", ")}.` : ""} ${pendingCheckType?.length > 0 ? `These items are pending: ${pendingCheckType.join(", ")}.` : ""}`}
              </div>
            ) : null}

            {
              (reviewCandidateData?.referoo_status === "pending" || reviewCandidateData?.referoo_status === "in_progress") && lastAction !== "approve_candidate_bypass_compliance_check" && completedCheckType?.length === 0 ? (
              <div className="fs-16 max-w-484px">
               Employee has generated their Referoo link. No items are completed yet. All items are pending.
              </div>
            ) : null}


           
          </>
        )}

        { (candidateVerificationStatus === 'verified' && (formattedExpired.length > 0 || formattedMultipleTitles.length > 0)) ? 
          <div className="d-flex w-100 max-w-484px">
            <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
              <Warning />
              <div className="fs-13 text-black fw-400">
                This employee’s{" "}
                {formattedExpired.length > 0 && formattedMultipleTitles.length > 0 ? (
                  <>
                    <span className="fw-700">{formattedExpired}</span> has expired and{" "}
                    <span className="fw-700">{formattedMultipleTitles}</span> expires soon.
                  </>
                ) : formattedExpired.length > 0 ? (
                  <>
                    <span className="fw-700">{formattedExpired}</span> has expired.
                  </>
                ) : formattedMultipleTitles.length > 0 ? (
                  <>
                    <span className="fw-700">{formattedMultipleTitles}</span> expires soon.
                  </>
                ) : (
                  ""
                )}{" "}
                They are required to upload a valid document in order to continue using
                Connected Crew.
              </div>
            </div>
          </div> : "" }
        {!isReferooDocumentFound || latestStage2Action === "admin-complete" || isAdminCompleteReopened || reviewCandidateData?.referoo_status === "pending" || reviewCandidateData?.referoo_status === "in_progress" ? (
          <>
            {/* Stage 2 compliance can no longer be bypassed; show the history of earlier bypasses only */}
            {(() => {
              const pastBypass = values?.bypass_compliance_check === 1
                ? reviewCandidateData?.profile?.step_bypass_reason?.find((type) => type.bypass_type === "bypass_compliance_check")
                : undefined;
              return pastBypass ? (
                <div className="fs-13 text-grey-dark max-w-484px">
                  Previously bypassed by {pastBypass.created_by} on {formatDateToReadable(pastBypass.created_at)}: {pastBypass.reason}
                </div>
              ) : null;
            })()}
            {/** 
             * Render the Compliance Check Documents
             */}
            { (candidateVerificationStatus !== 'verified' || (candidateVerificationStatus === 'verified' && (isDocumentGoingToExpire || isDocumentExpired))) ? 
              <>
                <div className="d-flex flex-column gap-10">
                  <div className="fs-16 fw-700">Police Check </div>
                  {
                    (latestStage2Action !== "admin-complete") && !isAdminCompleteReopened && !policeCheck ? (
                      <div className="text-grey-dark fs-13">Document not uploaded to Referoo yet</div>
                    ) : null
                  }

                  {
                    (latestStage2Action !== "admin-complete") && !isAdminCompleteReopened && policeCheck && reviewCandidateData?.profile?.has_police_check === 1 ? (
                      <>
                        <div className="d-flex">
                          <Link to={policeCheck.file_path} target="_blank" rel="noopener noreferrer">
                            <CustomButton variant={"primary"} type="button" className="btn-theme bg-off-white text-black">
                              <Download />
                              {formatFileName(policeCheck.file_name, maxLength, preserveEndChars)}
                            </CustomButton>
                          </Link>
                        </div>
                        <div className="d-flex flex-row gap-5">
                          <div className="fs-13 fw-400 text-grey-dark">{getDateLabel("police_check")}:</div>
                          <span className={`fs-13 ${isOneMonthBefore(policeCheck.validity_date ?? "") ? "fw-700 text-alert" : "fw-400 text-grey-dark"}`}>
                            {formatDateToReadable(policeCheck.issued_date) || "No data"}
                          </span>
                        </div>
                      </>
                    ) : null
                  }

                  {
                    (latestStage2Action === "admin-complete" || isAdminCompleteReopened) && (
                   <>
                       <DocumentUpload
                          onFileSelect={handleFileSelect}
                          type={"police_check"}
                          candidate_uuid={reviewCandidateData?.uuid}
                          uploadedFileName={getFileName("police_check")}
                        />
                        <div className="d-flex gap-10 align-items-center">
                          <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                            Issue date:
                          </div>
                          <div className="min-w-200px">
                            <DatePicker
                              selected={
                                policeCheck
                                  ? pcValidUntil
                                  : null
                              }
                              onChange={(date: Date | null) => {
                                setPcValidUntil(date);
                                handleDocDate("police_check", date);
                              }}
                              customInput={
                                <CustomDateInput
                                  placeholder="Select a date..."
                                />
                              }
                              dateFormat="dd MMM yyyy"
                              minDate={getMinDateByDocumentType("police_check")}
                              maxDate={new Date()}
                              disabled={!policeCheck}
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={15}
                            />
                          </div>
                          <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                            <Warning width={"15"} height={"15"} color={"#464646"} />
                            Expires {getValidityMonths('police_check')} months after issue date and will need renewing
                          </div>
                        </div>
                   </>
                   )
                  }
                </div>
                {(policeCheck && !pc_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document was issued" />
                )}
                <div className="bg-off-white min-h-1px"></div>
                <div className="d-flex flex-column gap-10">
                  <div className="fs-16 fw-700">Working With Children Check / Blue Card</div>
                   { (wccComplianceCheck && wccComplianceCheck.uploaded_by === 'candidate') ? (
                    <Link to={wccComplianceCheck.file_path ?? ""} target="_blank" rel="noopener noreferrer">
                      <CustomButton
                        variant={"primary"}
                        type="button"
                        className="btn-theme bg-off-white text-black"
                      >
                        <FileUpload />
                         {formatFileName(wccComplianceCheck.file_name ?? "", maxLength, preserveEndChars)}                         
                      </CustomButton>
                    </Link>
                  ) : (
                    <DocumentUpload
                      onFileSelect={handleFileSelect}
                      type={"working_with_children_check"}
                      candidate_uuid={reviewCandidateData?.uuid}
                      uploadedFileName={getFileName("working_with_children_check")}
                      removeIcon={wccComplianceCheck?.uploaded_by === 'candidate' ? true : false}
                    />
                  )}
                  <div className="d-flex gap-10 align-items-md-center flex-column flex-md-row">
                    <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                      Valid until:
                    </div>
                    <div className="min-w-200px">
                      <DatePicker
                        selected={
                          wccComplianceCheck
                            ? wccValidUntil
                            : null // ⛔ Don't show a date if the document is missing
                        }
                        onChange={(date: Date | null) => {
                          setWccValidUntil(date);
                          handleDocDate("working_with_children_check", date);
                        }}
                        customInput={
                          <CustomDateInput
                            placeholder="Select a date..."
                          />
                        }
                        dateFormat="dd MMM yyyy"
                        minDate={new Date()}
                        disabled={!wccComplianceCheck || (wccComplianceCheck && wccComplianceCheck?.uploaded_by === 'candidate' && wcc_valid_until_str) ? true : false}
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                      />
                    </div>
                  </div>
                </div>
                {(wccComplianceCheck && !wcc_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document is valid until" />
                )}
                <div className="bg-off-white min-h-1px"></div>
                <div className="d-flex flex-column gap-10">
                  <div className="fs-16 fw-700">Right To Work</div>

                  {
                    (latestStage2Action !== "admin-complete") && !isAdminCompleteReopened && (
                      rightToWorkDoc && rightToWorkDoc.length > 0 ? (
                        <div className="min-w-158px">
                          <CustomButton
                            variant="primary"
                            className="btn-theme bg-off-white text-black"
                            onClick={() => handleReferooDoc(rightToWorkDoc?.[0]?.pdf_url ?? "")}
                          >
                            <FileUpload />
                            {formatFileName(rightToWorkDoc?.[0]?.file_name, maxLength, preserveEndChars)}
                          </CustomButton>
                        </div>
                      ) : !reviewCandidateDocumentList?.find((doc) => doc.document_type === "right_to_work") && (
                        <div className="text-grey-dark fs-13">Document not uploaded to Referoo yet</div>
                      )
                    )
                  }

                   {
                    (latestStage2Action === "admin-complete"  || isAdminCompleteReopened )&& (
                    <DocumentUpload
                      onFileSelect={handleFileSelect}
                      type={"right_to_work"}
                      candidate_uuid={reviewCandidateData?.uuid}
                      uploadedFileName={getFileName("right_to_work")}
                    />
                    )
                  }
                  
                </div>
                <div className="bg-off-white min-h-1px"></div>
                <div className="d-flex flex-column gap-10">
                  <div className="fs-16 fw-700">Reference Check</div>

                  {
                    (latestStage2Action !== "admin-complete") && !isAdminCompleteReopened && (
                      referooReferenceCheckDoc && referooReferenceCheckDoc.length > 0 ? (
                        referooReferenceCheckDoc.map((referenceCheckDoc: ReferooDocument, index: number) => (
                          <div key={index} className="min-w-158px">
                            <CustomButton
                              variant="primary"
                              className="btn-theme bg-off-white text-black"
                              onClick={() => handleReferooDoc(referenceCheckDoc.pdf_url)}
                            >
                              <FileUpload />
                              {formatFileName(referenceCheckDoc.file_name, maxLength, preserveEndChars)}
                            </CustomButton>
                          </div>
                        ))
                      ) : !reviewCandidateDocumentList?.find((doc) => doc.document_type === "reference_check") && !reviewCandidateDocumentList?.find((doc) => doc.document_type === "reference_check_1") && (
                        <div className="text-grey-dark fs-13">Documents not uploaded to Referoo yet</div>
                      )
                    )
                  }

                   {
                    (latestStage2Action === "admin-complete"  || isAdminCompleteReopened )&& (
                      <>
                        <DocumentUpload
                          onFileSelect={handleFileSelect}
                          type={"reference_check"}
                          candidate_uuid={reviewCandidateData?.uuid}
                          uploadedFileName={getFileName("reference_check")}
                        />
                        <DocumentUpload
                          onFileSelect={handleFileSelect}
                          type={"reference_check_1"}
                          candidate_uuid={reviewCandidateData?.uuid}
                          uploadedFileName={getFileName("reference_check_1")}
                        />
                      </>
                   )
                   }
                </div>
              </> : 
              <>
                { sortedComplianceCheckDocs.map((doc, idx: number, filteredList) => (
                    <>
                      <div className="d-flex flex-column gap-10" key={idx}>
                        {
                          doc.document_type !== "reference_check_1" && (
                            <div className="fs-13 fw-700 text-black text-capitalize">
                              {getDocumentName(doc.document_type)}
                            </div>
                          )
                        }
                        <div className="d-flex">
                          <Link to={doc.file_path} target="_blank" rel="noopener noreferrer">
                            <CustomButton
                              variant={"primary"}
                              type="button"
                              className="btn-theme bg-off-white text-black"
                            >
                              <Download />
                              {doc.file_name}
                            </CustomButton>
                          </Link>
                        </div>
                        {!['right_to_work', 'reference_check', 'reference_check_1'].includes(doc.document_type) && (
                          <div className="d-flex flex-row gap-5">
                            <div className="fs-13 fw-400 text-grey-dark">
                              {getDateLabel(doc.document_type)}:{" "}
                            </div>
                            <span className={`fs-13 ${isOneMonthBefore(doc?.validity_date ?? "") ? `fw-700 text-alert` : `fw-400 text-grey-dark`}`}>
                              {isIssuedType(doc.document_type) ? (
                                <>
                                  {formatDateToReadable(doc.issued_date) || "No data"} | Valid until:{" "}
                                  <span className={`${
                                    isOneMonthBefore(doc.validity_date)
                                      ? "fw-700 text-alert"
                                      : "fw-400 text-grey-dark"
                                  }`}>
                                    {formatDateToReadable(doc.validity_date) || "No data"}
                                  </span>
                                </>
                              ) : (
                                formatDateToReadable(getDocumentDate(doc)) || "No data"
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Add separator only if it's not the last item */}
                      {idx < filteredList.length - 1 && doc.document_type !== "reference_check" && (
                        <div className="bg-off-white min-h-1px max-w-484px"></div>
                      )}
                    </>
                  ))
                }
              </>
            }
          </>
        ) : isReferooDocumentFound ? (
          <>
            <div className="fs-16 max-w-484px">
              These audits were retrieved from Referoo.
            </div>

            { ((policeCheckDoc && policeCheckDoc?.length > 0) || reviewCandidateData?.profile?.has_police_check === 1) ?
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Police Check
                </div>  
                <div className="min-w-158px">
                  {reviewCandidateData?.profile?.has_police_check === 1 ? (
                    <Link to={policeCheck?.file_path ?? ""} target="_blank" rel="noopener noreferrer">
                      <CustomButton
                        variant="primary"
                        className="btn-theme bg-off-white text-black"
                      >
                        <FileUpload />
                        {formatFileName(policeCheck?.file_name ?? "", maxLength, preserveEndChars)}
                      </CustomButton>
                    </Link>
                  ) : (
                    <CustomButton
                      variant="primary"
                      className="btn-theme bg-off-white text-black"
                      onClick={() => handleReferooDoc(policeCheckDoc?.[0]?.pdf_url ?? "")}
                    >
                      <FileUpload />
                      {formatFileName(policeCheckDoc?.[0]?.file_name ?? "", maxLength, preserveEndChars)}
                    </CustomButton>
                  )}
                </div>
                <div className="d-flex flex-row gap-5 align-items-center">
                  <div className="d-flex gap-5">
                    <span className="fs-13 fw-400 text-grey-dark">
                      Issue Date:{" "}{formatDateToReadable(reviewCandidateData?.profile?.has_police_check === 1 ? (policeCheck?.issued_date || "") : (policeCheckDoc?.[0]?.completion_date || "")) || "No data"}
                    </span>
                  </div>

                  <div className="text-grey-dark"> | </div>
                    <>
                      {(() => {
                        const baseDate = reviewCandidateData?.profile?.has_police_check === 1
                          ? (policeCheck?.issued_date || "")
                          : (policeCheckDoc?.[0]?.completion_date || "");
                        const policeCheckValidUntil = getDateAfterYears(baseDate, 1);
                        return (
                          <div className="d-flex gap-5">
                            <span className="fs-13 fw-400 text-grey-dark">Valid Until:{" "}</span>
                            <span className={`fs-13 ${
                              policeCheckValidUntil && isOneMonthBefore(policeCheckValidUntil)
                                ? "fw-700 text-alert"
                                : "fw-400 text-grey-dark"
                            }`}>
                              {policeCheckValidUntil ? (formatDateToReadable(policeCheckValidUntil) || "No data") : "No data"}
                            </span>
                          </div>
                        );
                      })()}
                    </>
                  </div>
                </div>
             
            : reviewCandidateData?.has_police_check === 1 && policeCheck ? (
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">Police Check</div>
                <div className="d-flex">
                  <Link to={policeCheck.file_path} target="_blank" rel="noopener noreferrer">
                    <CustomButton variant={"primary"} type="button" className="btn-theme bg-off-white text-black">
                      <Download />
                      {policeCheck.file_name}
                    </CustomButton>
                  </Link>
                </div>
                

                <div className="d-flex flex-row gap-5 align-items-center">
                  <div className="d-flex gap-5">
                    <span className="fs-13 fw-400 text-grey-dark">
                      Issue Date:{" "}{formatDateToReadable(reviewCandidateData?.profile?.has_police_check === 1 ? (policeCheck?.issued_date || "") : (policeCheckDoc?.[0]?.completion_date || "")) || "No data"}
                    </span>
                  </div>

                  <div className="text-grey-dark"> | </div>
                    <>
                      {(() => {
                        const baseDate = reviewCandidateData?.profile?.has_police_check === 1
                          ? (policeCheck?.issued_date || "")
                          : (policeCheckDoc?.[0]?.completion_date || "");
                        const policeCheckValidUntil = getDateAfterYears(baseDate, 1);
                        return (
                          <div className="d-flex gap-5">
                            <span className="fs-13 fw-400 text-grey-dark">Valid Until:{" "}</span>
                            <span className={`fs-13 ${
                              policeCheckValidUntil && isOneMonthBefore(policeCheckValidUntil)
                                ? "fw-700 text-alert"
                                : "fw-400 text-grey-dark"
                            }`}>
                              {policeCheckValidUntil ? (formatDateToReadable(policeCheckValidUntil) || "No data") : "No data"}
                            </span>
                          </div>
                        );
                      })()}
                    </>
                  </div>
               


              </div>
            ) : (
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Police Check
                </div>
                <div className="text-grey-dark fs-13">Document not uploaded to Referoo yet</div>
              </div>
            ) }

            <div className="bg-off-white min-h-1px"></div>

            { referooRightToWorkDoc && referooRightToWorkDoc?.length > 0 ? 
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Working With Children Check / Blue Card
                </div>
                <div className="min-w-158px">
                  <CustomButton
                    variant="primary"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => handleReferooDoc(referooRightToWorkDoc?.[0]?.pdf_url ?? "")}
                  >
                    <FileUpload />
                    {`${(`${reviewCandidateData?.first_name || ""} ${reviewCandidateData?.last_name || ""}`.trim()).replace(/\s+/g, "_")}_WWCC.pdf`}
                  </CustomButton>
                </div>
                <div className="d-flex flex-row gap-5">
                  <span className="fs-13 fw-400 text-grey-dark">
                    Valid Until:{" "}{formatDateToReadable(referooRightToWorkDoc?.[0]?.completion_date || "") || formatDateToReadable(wcc_valid_until_str || "") || "No data"} 
                  </span>
                </div>
              </div>
            : (
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Working With Children Check / Blue Card
                </div>
                 <div className="text-grey-dark fs-13">Document not uploaded to Referoo yet</div>
              </div>
            ) }

            <div className="bg-off-white min-h-1px"></div>

            { rightToWorkDoc && rightToWorkDoc?.length > 0 ? 
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Right To Work
                </div>
                <div className="min-w-158px">
                  <CustomButton
                    variant="primary"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => handleReferooDoc(rightToWorkDoc?.[0]?.pdf_url ?? "")}
                  >
                    <FileUpload />
                    {`${(`${reviewCandidateData?.first_name || ""} ${reviewCandidateData?.last_name || ""}`.trim()).replace(/\s+/g, "_")}_RightToWork.pdf`}
                  </CustomButton>
                </div>
              </div>
            : (
              (
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                   Right To Work
                </div>
                 <div className="text-grey-dark fs-13">Document not uploaded to Referoo yet</div>
              </div>
            )
            ) }
            
            <div className="bg-off-white min-h-1px"></div>

            
            
            

            { referooRightToWorkDoc && referooRightToWorkDoc?.length > 0 ? 
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Summary Report
                </div>
                <div className="min-w-158px">
                  <CustomButton
                    variant="primary"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => handleReferooDoc(referooRightToWorkDoc?.[0]?.pdf_url ?? "")}
                  >
                    <FileUpload />
                    {`${(`${reviewCandidateData?.first_name || ""} ${reviewCandidateData?.last_name || ""}`.trim()).replace(/\s+/g, "_")}_Summary_Report.pdf`}
                  </CustomButton>
                </div>
              </div>
            : (
              (
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                   Summary Report
                </div>
                 <div className="text-grey-dark fs-13">Document not uploaded to Referoo yet</div>
              </div>
            )
            ) }

            <div className="bg-off-white min-h-1px"></div>
            
            { referooReferenceCheckDoc.length > 0 ? 
              <div className="d-flex flex-column gap-10">
                <div className="fs-16 fw-700">
                  Reference Check
                </div>
                { referooReferenceCheckDoc.map((referenceCheckDoc: ReferooDocument, index: number) => (
                  <div key={index} className="d-flex flex-column flex-md-row align-items-md-center gap-15">
                    <div className="min-w-158px">
                      <CustomButton
                        variant="primary"
                        className="btn-theme bg-off-white text-black"
                        onClick={() => handleReferooDoc(referenceCheckDoc.pdf_url)}
                      >
                        <FileUpload />
                        {`${(`${reviewCandidateData?.first_name || ""} ${reviewCandidateData?.last_name || ""}`.trim()).replace(/\s+/g, "_")}_ReferenceBy_${(referenceCheckDoc.referee_name || "").replace(/\s+/g, "_")}.pdf`}
                      </CustomButton>
                    </div>
                    
                  </div>
                ))}
              </div>
            : "" }
          </>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default ComplianceCheck;